// assets/product-custom-property.js
import { Component } from "@theme/component";
import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/**
 * @typedef {object} ProductCustomLimitedEditionRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomLimitedEditionRefs>
 */
class ProductCustomLimitedEditionInput extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest(".shopify-section, dialog");
    if (closestSection) {
      closestSection.addEventListener(
        ThemeEvents.variantUpdate,
        this.updateUsedNumbersData,
      );
    }
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartChange);
    void this.handleRandomize(); // 2026-06: auto-assign on load / variant change
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartChange);
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updateUsedNumbersData,
    );
  }

  /**
   * Re-assign number after cart add, update, or remove.
   * @param {CustomEvent} event
   */
  #onCartChange = (event) => {
    if (event.detail?.data?.didError) return;
    void this.handleRandomize();
  };

  handleInput() {
    this.#onlyAllowNumbers();
    this.#checkIfNumberIsAlreadyUsed();
  }

  async handleRandomize() {
    const { textInput } = this.refs;
    const maxNumber = parseInt(
      textInput.getAttribute("data-max-number") || "1000",
      10,
    );
    const usedNumbersStr = textInput.getAttribute("data-used-numbers") || "";
    const usedNumbers = usedNumbersStr
      .split(",")
      .map((n) => n.trim())
      .filter((n) => n !== "")
      .map(Number);

    // Exclude lucky numbers already in the cart for this product
    const cartNumbers = await this.#getCartNumbers(textInput);
    cartNumbers.forEach((n) => {
      if (!usedNumbers.includes(n)) usedNumbers.push(n);
    });

    const availableNumbers = [];
    for (let i = 1; i <= maxNumber; i++) {
      if (!usedNumbers.includes(i)) {
        availableNumbers.push(i);
      }
    }

    if (availableNumbers.length === 0) {
      textInput.value = "";
      textInput.placeholder = textInput.getAttribute("data-no-numbers") || "No numbers available";
      textInput.classList.add("is-no-numbers");
      textInput.setCustomValidity(
        textInput.getAttribute("data-no-numbers") || "No numbers available",
      );
      return;
    }
    textInput.classList.remove("is-no-numbers");

    // 2026-06: was random pick on "Pick for me" click
    // const randomIndex = Math.floor(Math.random() * availableNumbers.length);
    // const randomValue = availableNumbers[randomIndex];
    // textInput.value = randomValue;
    textInput.value = availableNumbers[0]; // first available in ascending order
    this.#checkIfNumberIsAlreadyUsed();
  }

  async #getCartNumbers(textInput) {
    try {
      const propertyKey = (textInput.getAttribute("name") || "")
        .replace("properties[", "")
        .replace("]", "");
      if (!propertyKey) return [];

      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) return [];
      const cart = await res.json();

      let productEl = this.closest("[data-product-id]");
      if (!productEl) {
        const section = this.closest(".shopify-section");
        if (section) productEl = section.querySelector("[data-product-id]");
      }
      const productId = productEl
        ? productEl.getAttribute("data-product-id")
        : null;

      const numbers = [];
      (cart.items || []).forEach((item) => {
        if (productId && String(item.product_id) !== String(productId)) return;
        const val = item.properties && item.properties[propertyKey];
        if (val && !isNaN(Number(val))) numbers.push(Number(val));
      });
      return numbers;
    } catch (e) {
      return [];
    }
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateUsedNumbersData(event) {
    // console.log("updateUsedNumbersData", event);

    if (event.detail.data.html) {
      const newUsedNumbersElement = event.detail.data.html.querySelector(
        "[data-used-numbers]",
      );
      const currentUsedNumbersElement = this.querySelector(
        "[data-used-numbers]",
      );

      if (newUsedNumbersElement && currentUsedNumbersElement) {
        currentUsedNumbersElement.setAttribute(
          "data-used-numbers",
          newUsedNumbersElement.getAttribute("data-used-numbers") || "",
        );
      }
    }

    void this.handleRandomize(); // 2026-06: auto-assign on variant change
  }

  #checkIfNumberIsAlreadyUsed() {
    const { textInput } = this.refs;
    const value = textInput.value;
    const usedNumbers = (textInput.getAttribute("data-used-numbers") || "")
      .split(",")
      .map((n) => n.trim())
      .filter((n) => n !== "");
    if (usedNumbers && usedNumbers.includes(value)) {
      textInput.setCustomValidity(
        textInput.getAttribute("data-taken-number") ||
          "This number is already reserved",
      );
    } else {
      textInput.setCustomValidity("");
    }
  }

  #onlyAllowNumbers() {
    const { textInput } = this.refs;
    const value = textInput.value;
    const regex = /^[0-9]*$/;
    if (!regex.test(value)) {
      textInput.value = value.replace(/[^0-9]/g, "");
    }
    if (
      parseInt(value) >
      parseInt(textInput.getAttribute("data-max-number") || "1000")
    ) {
      textInput.value = textInput.getAttribute("data-max-number") || "1000";
    }
  }
}

customElements.define(
  "product-custom-limited-edition",
  ProductCustomLimitedEditionInput,
);
